import React, { useState } from 'react'
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useTranslation } from '../hooks/use-i18n'

interface StripeCheckoutFormProps {
  amountCents: number
  currency: string
  onSuccess: () => void
  onCancel: () => void
}

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({
  amountCents,
  currency,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentElementReady, setPaymentElementReady] = useState(false)
  const [paymentDetailsComplete, setPaymentDetailsComplete] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements || !paymentElementReady) return
    if (!paymentDetailsComplete) {
      setError(t('payments.selectPaymentMethod'))
      return
    }

    setIsProcessing(true)
    setError(null)

    const returnUrl = `${window.location.origin}/payments?payment_success=1`

    try {
      const paymentElement = elements.getElement('payment')
      if (!paymentElement) {
        setError(t('payments.errorConfirm'))
        return
      }

      const { error: submitError } = await elements.submit()
      if (submitError) {
        setError(submitError.message || t('payments.errorConfirm'))
        return
      }

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: 'if_required',
      })

      if (confirmError) {
        setError(confirmError.message || t('payments.errorConfirm'))
        return
      }

      onSuccess()
    } catch (err) {
      const message = err instanceof Error ? err.message : t('payments.errorConfirm')
      setError(message)
    } finally {
      setIsProcessing(false)
    }
  }

  const amountDisplay = (amountCents / 100).toFixed(2)
  const currencyUpper = currency.toUpperCase()

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-xl border border-border bg-muted/20 p-4">
        <PaymentElement
          options={{ layout: 'tabs' }}
          onReady={() => setPaymentElementReady(true)}
          onChange={(e) => {
            setPaymentDetailsComplete(e.complete)
            if (e.complete) setError(null)
          }}
          onLoadError={(e) => {
            setPaymentElementReady(false)
            setPaymentDetailsComplete(false)
            setError(e.error?.message || t('payments.errorConfirm'))
          }}
        />
      </div>

      {!paymentDetailsComplete && paymentElementReady && (
        <p className="text-muted-foreground text-small">
          {t('payments.selectPaymentHint')}
        </p>
      )}

      {error && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-2 text-destructive text-small">
          {error}
        </div>
      )}

      <p className="text-muted-foreground text-small">
        {t('payments.amountLabel')}: <strong className="text-foreground">{amountDisplay} {currencyUpper}</strong>
      </p>

      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={!stripe || !elements || !paymentElementReady || !paymentDetailsComplete || isProcessing}
          className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isProcessing
            ? t('payments.processing')
            : !paymentElementReady
            ? t('payments.loading')
            : t('payments.payNow')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="px-5 py-2.5 rounded-xl border border-border bg-muted/30 text-foreground font-medium hover:bg-muted/50 disabled:opacity-50 transition-all"
        >
          {t('payments.cancel')}
        </button>
      </div>
    </form>
  )
}

export default StripeCheckoutForm
